'use client';

import { c as _c } from "react/compiler-runtime";
import 'swiper/css';
import 'swiper/css/thumbs';
import Image from 'next/image';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import cn from '@/helpers/cn';
import useDevice from '@/hooks/useDevice';
import css from './style.module.scss';
import { GalleryDto } from './types';
interface Props {
  data: GalleryDto;
}
const Gallery: FC<Props> = t0 => {
  const $ = _c(30);
  const {
    data
  } = t0;
  const device = useDevice();
  const mainSwiperRef = useRef(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [cursorContent, setCursorContent] = useState(null);
  const cursorRef = useRef(null);
  const galleryRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== device) {
    t1 = () => {
      if (device === "mobile") {
        return;
      }
      const gallery = galleryRef.current;
      const cursor = cursorRef.current;
      if (!gallery || !cursor) {
        return;
      }
      let rect = gallery.getBoundingClientRect();
      const lastMouse = {
        x: 0,
        y: 0
      };
      let ticking = false;
      const updateRect = () => {
        rect = gallery.getBoundingClientRect();
      };
      window.addEventListener("scroll", updateRect, {
        passive: true
      });
      window.addEventListener("resize", updateRect);
      const onMouseMove = e => {
        lastMouse.x = e.clientX;
        lastMouse.y = e.clientY;
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(() => {
            const localX = lastMouse.x - rect.left;
            const localY = lastMouse.y - rect.top;
            cursor.style.left = `${localX}px`;
            cursor.style.top = `${localY}px`;
            ticking = false;
          });
        }
      };
      window.addEventListener("mousemove", onMouseMove);
      const zones = gallery.querySelectorAll("[data-type]");
      const handleEnter = e_0 => {
        const type = (e_0.currentTarget as HTMLElement).dataset.type;
        if (type === "prev") {
          setCursorContent(<><Image src="/assets/icons/arrow.svg" alt="prev" width={18} height={18} className={css.cursorArrowPrev} /><span>Previous</span></>);
        } else {
          setCursorContent(<><span>Next</span><Image src="/assets/icons/arrow.svg" alt="next" width={18} height={18} className={css.cursorArrowNext} /></>);
        }
        cursorRef.current?.classList.add(css.customCursorVisible);
      };
      const handleLeave = () => {
        cursorRef.current?.classList.remove(css.customCursorVisible);
        setCursorContent(null);
      };
      zones.forEach(z => {
        z.addEventListener("mouseenter", handleEnter);
        z.addEventListener("mouseleave", handleLeave);
      });
      updateRect();
      return () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("scroll", updateRect);
        window.removeEventListener("resize", updateRect);
        zones.forEach(z_0 => {
          z_0.removeEventListener("mouseenter", handleEnter);
          z_0.removeEventListener("mouseleave", handleLeave);
        });
      };
    };
    t2 = [device];
    $[0] = device;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [Navigation, Thumbs];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      0: {
        slidesPerView: "auto"
      },
      992: {
        slidesPerView: 1
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== thumbsSwiper) {
    t5 = {
      swiper: thumbsSwiper,
      slideThumbActiveClass: css.thumbsSwiperActive
    };
    $[5] = thumbsSwiper;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      delay: 5000,
      pauseOnMouseEnter: true
    };
    t7 = swiper => mainSwiperRef.current = swiper;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[9] !== data.images) {
    t8 = data.images.map(_temp);
    $[9] = data.images;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t8) {
    t9 = <Swiper loop={true} grabCursor={true} centeredSlides={true} modules={t3} spaceBetween={10} className={css.mainSwiper} breakpoints={t4} thumbs={t5} autoplay={t6} onSwiper={t7}>{t8}</Swiper>;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className={css.fade} />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [Thumbs];
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== data.images) {
    t12 = data.images.map(_temp2);
    $[16] = data.images;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12) {
    t13 = <Swiper loop={true} watchSlidesProgress={true} slideToClickedSlide={true} height={100} modules={t11} spaceBetween={15} slidesPerView="auto" className={css.thumbsSwiper} onSwiper={setThumbsSwiper}>{t12}</Swiper>;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cn(css.cursorZone, css.cursorPrev);
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className={t14} data-type="prev" onClick={() => mainSwiperRef.current?.slidePrev()} />;
    t16 = cn(css.cursorZone, css.cursorNext);
    $[21] = t15;
    $[22] = t16;
  } else {
    t15 = $[21];
    t16 = $[22];
  }
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className={t16} data-type="next" onClick={() => mainSwiperRef.current?.slideNext()} />;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== cursorContent) {
    t18 = <div ref={cursorRef} className={css.customCursor}>{cursorContent}</div>;
    $[24] = cursorContent;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t13 || $[27] !== t18 || $[28] !== t9) {
    t19 = <section className={css.wrapper}><div ref={galleryRef} className={css.gallery}>{t9}{t10}{t13}{t15}{t17}{t18}</div></section>;
    $[26] = t13;
    $[27] = t18;
    $[28] = t9;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  return t19;
};
export default Gallery;
function _temp(t0, i) {
  const {
    image
  } = t0;
  return <SwiperSlide key={i} className={css.mainSwiperSlide}><img src={image.url} alt={image.alternativeText ?? ""} className={css.mainImage} height={700} width={1200} loading="lazy" /></SwiperSlide>;
}
function _temp2(t0, i_0) {
  const {
    image: image_0
  } = t0;
  return <SwiperSlide key={i_0} className={css.thumbSlide}><img src={image_0.url} alt={image_0.alternativeText ?? ""} width={160} height={100} loading="lazy" /></SwiperSlide>;
}